import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Edit,
  FileText,
  Video,
  Music,
  Image,
  Link,
  BookOpen,
  Search,
  Filter,
  Upload,
  Sparkles,
  Brain,
  MessageCircle,
  AlertTriangle,
  Send,
  Download,
  Eye,
  FolderOpen,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const CATEGORIES = [
  "ODR Linea Professionale",
  "Tecnologia Ioxina",
  "Protocolli Operativi",
  "Scientifico e Studi",
  "Estetiste",
  "Parrucchieri",
  "Agenti e Distributori",
  "Formazione e Academy",
  "FAQ",
  "Link e Risorse Esterne"
];

const AUDIENCES = [
  { value: "tutti", label: "Tutti" },
  { value: "estetista", label: "Estetiste" },
  { value: "parrucchiere", label: "Parrucchieri" },
  { value: "agente", label: "Agenti" },
  { value: "distributore", label: "Distributori" },
];

const CONTENT_TYPES = [
  { value: "pdf", label: "PDF", icon: FileText },
  { value: "word", label: "Documento Word", icon: FileText },
  { value: "presentation", label: "Presentazione", icon: FileText },
  { value: "video", label: "Video", icon: Video },
  { value: "podcast", label: "Podcast", icon: Music },
  { value: "image", label: "Immagine", icon: Image },
  { value: "link", label: "Link Esterno", icon: Link },
  { value: "text", label: "Testo Descrittivo", icon: BookOpen },
];

const getContentIcon = (type) => {
  const contentType = CONTENT_TYPES.find(t => t.value === type);
  if (contentType) {
    const IconComponent = contentType.icon;
    return <IconComponent className="w-5 h-5" />;
  }
  return <FileText className="w-5 h-5" />;
};

const AdminSoleaKnowledgePage = () => {
  const [knowledge, setKnowledge] = useState([]);
  const [unanswered, setUnanswered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("knowledge");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    tags: "",
    target_audience: "tutti",
    content_type: "text",
    file_url: "",
    external_link: "",
    text_content: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [knowledgeRes, unansweredRes] = await Promise.all([
        axios.get(`${API}/admin/solea/knowledge`),
        axios.get(`${API}/admin/solea/unanswered`),
      ]);
      setKnowledge(knowledgeRes.data);
      setUnanswered(unansweredRes.data.questions || []);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);
    
    try {
      const response = await axios.post(`${API}/upload`, uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      const fullUrl = response.data.url.startsWith('http') 
        ? response.data.url 
        : `${window.location.origin}${response.data.url}`;
      
      setFormData({ ...formData, file_url: fullUrl });
      toast.success("File caricato con successo");
    } catch (error) {
      toast.error("Errore nel caricamento del file");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.category) {
      toast.error("Titolo e categoria sono obbligatori");
      return;
    }
    
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(",").map(t => t.trim()) : [],
      };
      
      if (editingItem) {
        await axios.put(`${API}/admin/solea/knowledge/${editingItem.knowledge_id}`, payload);
        toast.success("Contenuto aggiornato");
      } else {
        await axios.post(`${API}/admin/solea/knowledge`, payload);
        toast.success("Contenuto aggiunto alla Base di Conoscenza");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/solea/knowledge/${itemToDelete.knowledge_id}`);
      toast.success("Contenuto eliminato");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDeleteUnanswered = async (questionId) => {
    try {
      await axios.delete(`${API}/admin/solea/unanswered/${questionId}`);
      toast.success("Domanda rimossa");
      fetchData();
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const sendWeeklyReport = async () => {
    try {
      await axios.post(`${API}/admin/solea/send-report`);
      toast.success("Report inviato via email");
    } catch (error) {
      toast.error("Errore nell'invio del report");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      category: "",
      tags: "",
      target_audience: "tutti",
      content_type: "text",
      file_url: "",
      external_link: "",
      text_content: "",
    });
    setEditingItem(null);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || "",
      category: item.category,
      tags: item.tags?.join(", ") || "",
      target_audience: item.target_audience,
      content_type: item.content_type,
      file_url: item.file_url || "",
      external_link: item.external_link || "",
      text_content: item.text_content || "",
    });
    setDialogOpen(true);
  };

  const filteredKnowledge = knowledge.filter(item => {
    const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tags?.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const groupedKnowledge = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = filteredKnowledge.filter(item => item.category === cat);
    return acc;
  }, {});

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#B87A8A] to-[#9B6A7A] flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground">
                Base di Conoscenza SOLEA
              </h1>
              <p className="text-muted-foreground text-sm">
                Gestisci i contenuti che SOLEA utilizzerà per rispondere agli utenti
              </p>
            </div>
          </div>
          <Button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="rounded-full bg-[#B87A8A] hover:bg-[#A06878]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Contenuto
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-secondary/50 p-1 rounded-full">
            <TabsTrigger value="knowledge" className="rounded-full data-[state=active]:bg-white">
              <BookOpen className="w-4 h-4 mr-2" />
              Base di Conoscenza ({knowledge.length})
            </TabsTrigger>
            <TabsTrigger value="unanswered" className="rounded-full data-[state=active]:bg-white">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Domande Non Risolte ({unanswered.length})
            </TabsTrigger>
          </TabsList>

          {/* Knowledge Base Tab */}
          <TabsContent value="knowledge" className="space-y-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca contenuti..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-full"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-64 rounded-full">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtra per categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Content by Category */}
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-32 bg-secondary/50 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                {CATEGORIES.map(category => {
                  const items = groupedKnowledge[category];
                  if (selectedCategory !== "all" && selectedCategory !== category) return null;
                  if (items.length === 0 && selectedCategory === "all") return null;
                  
                  return (
                    <Card key={category} className="rounded-2xl">
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                          <FolderOpen className="w-5 h-5 text-[#B87A8A]" />
                          <CardTitle className="text-lg">{category}</CardTitle>
                          <Badge variant="secondary" className="ml-auto">
                            {items.length} contenuti
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {items.length === 0 ? (
                          <p className="text-muted-foreground text-sm py-4 text-center">
                            Nessun contenuto in questa categoria
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {items.map(item => (
                              <div
                                key={item.knowledge_id}
                                className="flex items-start gap-4 p-4 bg-secondary/30 rounded-xl hover:bg-secondary/50 transition-colors"
                              >
                                <div className="w-10 h-10 rounded-lg bg-[#B87A8A]/10 flex items-center justify-center text-[#B87A8A]">
                                  {getContentIcon(item.content_type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-foreground">{item.title}</h4>
                                  {item.description && (
                                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                      {item.description}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    <Badge variant="outline" className="text-xs">
                                      {AUDIENCES.find(a => a.value === item.target_audience)?.label || item.target_audience}
                                    </Badge>
                                    {item.tags?.slice(0, 3).map(tag => (
                                      <Badge key={tag} variant="secondary" className="text-xs">
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {item.file_url && (
                                    <a href={item.file_url} target="_blank" rel="noopener noreferrer">
                                      <Button variant="ghost" size="icon" className="rounded-full">
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                    </a>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="rounded-full"
                                    onClick={() => openEditDialog(item)}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="rounded-full text-destructive hover:text-destructive"
                                    onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Unanswered Questions Tab */}
          <TabsContent value="unanswered" className="space-y-6">
            <Card className="rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Domande Senza Risposta</CardTitle>
                    <CardDescription>
                      Domande a cui SOLEA non ha potuto rispondere. Usa queste per migliorare la Base di Conoscenza.
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={sendWeeklyReport}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Invia Report Email
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {unanswered.length === 0 ? (
                  <div className="text-center py-12">
                    <Sparkles className="w-12 h-12 text-[#B87A8A]/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Nessuna domanda non risolta! SOLEA sta rispondendo a tutto. 🎉
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {unanswered.map(q => (
                      <div
                        key={q.question_id}
                        className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200"
                      >
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-foreground">{q.question}</p>
                          <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Ruolo: {q.user_role || "N/A"}</span>
                            <span>Data: {new Date(q.created_at).toLocaleDateString("it-IT")}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                            onClick={() => {
                              resetForm();
                              setFormData(prev => ({
                                ...prev,
                                title: `Risposta: ${q.question.substring(0, 50)}...`,
                                text_content: `Domanda originale: ${q.question}\n\nRisposta:\n`,
                              }));
                              setDialogOpen(true);
                            }}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Crea Contenuto
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-full text-destructive"
                            onClick={() => handleDeleteUnanswered(q.question_id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingItem ? "Modifica Contenuto" : "Nuovo Contenuto"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="title">Titolo *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Titolo del contenuto"
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Categoria *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="audience">Destinatari</Label>
                <Select
                  value={formData.target_audience}
                  onValueChange={(v) => setFormData({ ...formData, target_audience: v })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIENCES.map(aud => (
                      <SelectItem key={aud.value} value={aud.value}>{aud.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content_type">Tipo Contenuto</Label>
                <Select
                  value={formData.content_type}
                  onValueChange={(v) => setFormData({ ...formData, content_type: v })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tags">Tag (separati da virgola)</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="es: oncologia, skincare, protocollo"
                  className="rounded-xl"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label htmlFor="description">Descrizione</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Breve descrizione del contenuto"
                  className="rounded-xl"
                  rows={2}
                />
              </div>

              {/* File Upload */}
              {["pdf", "word", "presentation", "video", "podcast", "image"].includes(formData.content_type) && (
                <div className="col-span-2 space-y-2">
                  <Label>File</Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.file_url}
                      onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                      placeholder="URL del file o carica un file"
                      className="rounded-xl flex-1"
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-xl"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? "Caricamento..." : "Carica"}
                    </Button>
                  </div>
                </div>
              )}

              {/* External Link */}
              {formData.content_type === "link" && (
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="external_link">Link Esterno</Label>
                  <Input
                    id="external_link"
                    value={formData.external_link}
                    onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                    placeholder="https://..."
                    className="rounded-xl"
                  />
                </div>
              )}

              {/* Text Content */}
              <div className="col-span-2 space-y-2">
                <Label htmlFor="text_content">
                  Contenuto Testuale
                  <span className="text-xs text-muted-foreground ml-2">
                    (SOLEA userà questo testo per rispondere)
                  </span>
                </Label>
                <Textarea
                  id="text_content"
                  value={formData.text_content}
                  onChange={(e) => setFormData({ ...formData, text_content: e.target.value })}
                  placeholder="Inserisci il contenuto testuale che SOLEA potrà utilizzare per rispondere alle domande degli utenti..."
                  className="rounded-xl"
                  rows={6}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button className="rounded-full bg-[#B87A8A] hover:bg-[#A06878]" onClick={handleSubmit}>
              {editingItem ? "Salva Modifiche" : "Aggiungi Contenuto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina Contenuto</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare "{itemToDelete?.title}"? 
              SOLEA non potrà più utilizzare questo contenuto per rispondere.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-destructive hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default AdminSoleaKnowledgePage;
